import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, OnInit, computed, forwardRef, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { delay, filter, take } from 'rxjs';
import { IMediaLock } from 'src/app/api/modules/core/dynamic/components/IMediaLock';
import { createDigitaServiceError } from 'src/app/app-error';
import { MediaLockableComponent } from '../../../components/static/media-lockable/media-lockable.component';
import { ButtonComponent } from '../button/button.component';

/**
 * The Media Lock Component is used to Force a user to watch Media.
 *
 * It embeds a {@link MediaLockableComponent} and reveals the configured
 * continue button once the media is unlocked (or errors, so the user is
 * never stranded).
 */
@Component({
  selector: 'app-media-lock',
  templateUrl: './media-lock.component.html',
  styleUrls: ['./media-lock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // There is a circular dependency here, so we need to use forwardRef
  imports: [MediaLockableComponent, forwardRef(() => ButtonComponent)],
})
export class MediaLockComponent implements OnInit {
  private readonly loggerService = inject(LoggerService);

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // CONFIG
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * The Configuration. Validated once when the component initializes.
   */
  readonly config = input.required<IMediaLock>();

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // STATE
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Has the media lock been unlocked?
   */
  private readonly unlocked = signal(false);

  /**
   * Has the media lock errored?
   */
  private readonly error = signal(false);

  /**
   * The unlock button appears 500ms after the unlock so it doesn't pop in the
   * same instant the media completes.
   */
  private readonly unlockedDelayed = toSignal(
    toObservable(this.unlocked).pipe(
      filter((unlocked) => unlocked),
      take(1),
      delay(500),
    ),
    { initialValue: false },
  );

  /**
   * Should the continue button be shown? Unlocks reveal it after the settle
   * delay; errors reveal it immediately so the user is never stranded.
   */
  protected readonly showButton = computed(() => this.unlockedDelayed() || this.error());

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // LIFECYCLE
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Lifecycle Hook
   */
  ngOnInit() {
    const configuration = this.config();

    // if there is no configuration then that is an error
    if (!configuration) {
      throw createDigitaServiceError(`MediaLock`, `configure`, `No configuration provided but this is required.`, `config`);
    }

    // if there is no lock then that is an error
    if (!configuration.lock) {
      throw createDigitaServiceError(`MediaLock`, `configure`, `No "lock" property provided but this is required.`, `config`);
    }

    // if there is no button then that is an error
    if (!configuration.button) {
      throw createDigitaServiceError(`MediaLock`, `configure`, `No "button" property provided but this is required.`, `config`);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // MEDIA LOCK EVENTS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Occurs from the Media Lock when the Media is Unlocked.
   */
  mediaLockUnlocked() {
    this.unlocked.set(true);
  }

  /**
   * Occurs from the Media Lock when the Media has experienced an error.
   */
  mediaLockError() {
    this.loggerService.error(`Media Error - Unlocked`);
    this.error.set(true);
  }
}
